import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Datepicker, locale, type MbscDatepickerChangeEvent } from "@mobiscroll/react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import useTimeFormat from "../../../../hooks/useTimeFormat/useTimeFormat";

export type ScheduleExcelExportDialogProps = {
  open: boolean;
  onClose: () => void;
  periodStart: string;
  periodEnd: string;
  onRangeChange: (start: string | null, end: string | null) => void;
  rangeIsValid: boolean;
  isExporting?: boolean;
  onConfirm: () => void;
};

export function ScheduleExcelExportDialog({
  open,
  onClose,
  periodStart,
  periodEnd,
  onRangeChange,
  rangeIsValid,
  isExporting = false,
  onConfirm
}: Readonly<ScheduleExcelExportDialogProps>) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { dateFormat } = useTimeFormat();

  const rangePickerValue: [Date | null, Date | null] = [
    periodStart ? new Date(periodStart) : null,
    periodEnd ? new Date(periodEnd) : null
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-testid="schedule-excel-export-dialog"
      slotProps={{ paper: { sx: { px: 1, py: 1 } } }}
    >
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
          {t("_scheduleExcelExport_periodTitle")}
        </Typography>
        <Box
          data-testid="schedule-excel-export-period-range"
          sx={{
            "& .mbsc-material.mbsc-textfield-wrapper": { m: 0 },
            width: "100%"
          }}
        >
          <Datepicker
            display={"inline"}
            select="range"
            data-testid="schedule-excel-export-date-picker"
            rangeHighlight={true}
            dateFormat={dateFormat.toUpperCase()}
            locale={locale[i18n.language]}
            value={rangePickerValue}
            label={t("_scheduleExcelExport_dateRange")}
            touchUi={isMobile}
            onChange={(picker: MbscDatepickerChangeEvent) => {
              const raw = picker.value;
              if (!Array.isArray(raw)) {
                return;
              }
              const [from, to] = raw as (Date | null)[];
              onRangeChange(
                from != null ? DateTime.fromJSDate(from).startOf("day").toISO() ?? null : null,
                to != null ? DateTime.fromJSDate(to).endOf("day").toISO() ?? null : null
              );
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, justifyContent: "flex-end", gap: 1 }}>
        <Button data-testid="schedule-excel-export-cancel" onClick={onClose} color="secondary">
          {t("Cancel")}
        </Button>
        <Button
          data-testid="schedule-excel-export-confirm"
          disabled={!rangeIsValid || isExporting}
          onClick={onConfirm}
        >
          {isExporting ? t("Loading...") : t("Export")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
